import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Table,
  Checkbox,
  Input,
  Chip,
  Stack,
  Alert,
  FormControl,
  FormLabel,
  Select,
  Option,
  Card,
  Divider,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon, ArrowsClockwise as AutorenewIcon, CheckCircle as CheckCircleIcon } from '@phosphor-icons/react';
import { insuranceApi, type InsurancePayoutItem, type InsuranceItemMapping, type MappingSuggestion } from '../../lib/insuranceApi';
import { distributorsApi, priceListApi, type PriceListItem, type Distributor } from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  item: InsurancePayoutItem | null;
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export default function InsuranceItemMappingModal({ item, open, onClose, onComplete }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<number | null>(null);
  const [priceListItems, setPriceListItems] = useState<PriceListItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [existingMappings, setExistingMappings] = useState<InsuranceItemMapping[]>([]);
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [preferredMapping, setPreferredMapping] = useState<number | null>(null);

  useEffect(() => {
    if (open && item) {
      loadDistributors();
      loadExistingMappings();
      loadSuggestions();
    }
  }, [open, item]);

  useEffect(() => {
    if (selectedDistributor) {
      loadPriceListItems();
    }
  }, [selectedDistributor, searchQuery]);

  const loadDistributors = async () => {
    try {
      const response = await distributorsApi.list({ is_active: true });
      setDistributors(response?.results || []);
    } catch (err) {
      showError('Failed to load distributors');
    }
  };

  const loadPriceListItems = async () => {
    if (!selectedDistributor) return;
    
    setLoading(true);
    try {
      const params: any = { 
        distributor: selectedDistributor,
        is_active: true,
        page_size: 1000  // Load up to 1000 items to avoid pagination
      };
      if (searchQuery) params.search = searchQuery;
      
      const response = await priceListApi.list(params);
      setPriceListItems(response?.results || []);
    } catch (err) {
      showError('Failed to load price list items');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingMappings = async () => {
    if (!item) return;
    
    try {
      const mappings = await insuranceApi.payoutItems.getMappings(item.id);
      setExistingMappings(mappings || []);
      
      // Find the preferred mapping
      const preferred = mappings?.find((m: InsuranceItemMapping) => m.is_preferred);
      if (preferred) {
        setPreferredMapping(preferred.id);
      }
    } catch (err) {
      console.error('Failed to load existing mappings:', err);
    }
  };

  const loadSuggestions = async () => {
    if (!item) return;
    
    setLoadingSuggestions(true);
    try {
      const response = await insuranceApi.mappings.suggestMappings(item.id, 5);
      setSuggestions(response?.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleCreateMappings = async () => {
    if (!item || selectedItems.length === 0) return;
    
    setLoading(true);
    try {
      const response = await insuranceApi.mappings.bulkCreate(
        item.id,
        selectedItems,
        preferredMapping || undefined
      );
      
      showSuccess(`Created ${response.mappings.length} mapping(s) successfully`);
      setSelectedItems([]);
      await loadExistingMappings();
      
      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to create mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPreferred = async (mappingId: number) => {
    try {
      await insuranceApi.mappings.setPreferred(mappingId);
      setPreferredMapping(mappingId);
      await loadExistingMappings();
      showSuccess('Updated preferred distributor');
    } catch (err) {
      showError('Failed to update preferred distributor');
    }
  };

  const handleRemoveMapping = async (mappingId: number) => {
    try {
      await insuranceApi.mappings.delete(mappingId);
      await loadExistingMappings();
      showSuccess('Removed mapping');
    } catch (err) {
      showError('Failed to remove mapping');
    }
  };

  const formatPrice = (price: any): string => {
    if (!price) return '-';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const calculateMargin = (insurancePrice: string | null | undefined, distributorPrice: string): { amount: number; percent: number } | null => {
    if (!insurancePrice) return null;
    
    const insPrice = parseFloat(insurancePrice);
    const distPrice = parseFloat(distributorPrice);
    const margin = insPrice - distPrice;
    const marginPercent = (margin / insPrice) * 100;
    
    return {
      amount: margin,
      percent: marginPercent
    };
  };

  if (!item) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 1200 }}>
        <ModalClose />
        <DialogTitle>Map Insurance Item to Distributors</DialogTitle>
        
        <DialogContent>
          {/* Insurance Item Details */}
          <Card variant="soft" sx={{ mb: 3 }}>
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Insurance Item</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip size="sm" color="primary">{item.selector_code}</Chip>
                <Typography level="body-md">{item.description}</Typography>
                <Typography level="body-sm" sx={{ ml: 'auto' }}>
                  Price: {formatPrice(item.replace_price || item.remove_price || item.detach_reset_price)}
                </Typography>
              </Stack>
            </Box>
          </Card>

          {/* Existing Mappings */}
          {existingMappings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography level="title-md" sx={{ mb: 2 }}>Current Mappings</Typography>
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Distributor</th>
                    <th>Product</th>
                    <th>Price</th>
                    <th>Margin</th>
                    <th>Preferred</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {existingMappings.map((mapping) => {
                    const margin = calculateMargin(mapping.insurance_price, mapping.product_price || '0');
                    return (
                      <tr key={mapping.id}>
                        <td>{mapping.distributor_name}</td>
                        <td>{mapping.product_name}</td>
                        <td>{formatPrice(mapping.product_price)}</td>
                        <td>
                          {margin && (
                            <Chip 
                              size="sm" 
                              color={margin.amount > 0 ? 'success' : 'danger'}
                              variant="soft"
                            >
                              {formatPrice(margin.amount.toString())} ({margin.percent.toFixed(1)}%)
                            </Chip>
                          )}
                        </td>
                        <td>
                          <Checkbox
                            checked={mapping.is_preferred}
                            onChange={() => handleSetPreferred(mapping.id)}
                          />
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleRemoveMapping(mapping.id)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Add New Mappings */}
          <Box>
            <Typography level="title-md" sx={{ mb: 2 }}>Add Distributor Products</Typography>
            
            {/* AI Suggestions */}
            {loadingSuggestions ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography>Finding best matches...</Typography>
              </Box>
            ) : suggestions.length > 0 && (
              <Alert
                color="primary"
                variant="soft"
                startDecorator={<AutorenewIcon />}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography level="title-sm">Suggested Matches</Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {suggestions.map((suggestion) => {
                      const margin = calculateMargin(
                        item.replace_price || item.remove_price || item.detach_reset_price,
                        suggestion.unit_price
                      );
                      return (
                        <Box 
                          key={suggestion.id}
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 'sm',
                            '&:hover': { bgcolor: 'background.level1' }
                          }}
                        >
                          <Typography level="body-sm">
                            {suggestion.distributor}: {suggestion.product_name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography level="body-sm">
                              {formatPrice(suggestion.unit_price)}
                            </Typography>
                            {margin && (
                              <Chip size="sm" color={margin.amount > 0 ? 'success' : 'danger'} variant="soft">
                                {margin.percent.toFixed(0)}%
                              </Chip>
                            )}
                            <Button
                              size="sm"
                              variant="soft"
                              onClick={() => {
                                setSelectedItems([...selectedItems, suggestion.id]);
                                setSuggestions(suggestions.filter(s => s.id !== suggestion.id));
                              }}
                            >
                              Add
                            </Button>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Alert>
            )}

            {/* Manual Selection */}
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Distributor</FormLabel>
                  <Select
                    value={selectedDistributor}
                    onChange={(_, value) => setSelectedDistributor(value as number)}
                    placeholder="Select distributor"
                  >
                    {distributors.map((dist) => (
                      <Option key={dist.id} value={dist.id}>
                        {dist.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Search Products</FormLabel>
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or ID"
                    startDecorator={<SearchIcon />}
                  />
                </FormControl>
              </Box>

              {/* Products Table */}
              {selectedDistributor && (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table size="sm" stickyHeader>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>Select</th>
                        <th>Product Name</th>
                        <th>Unit</th>
                        <th>Price</th>
                        <th>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceListItems.map((product) => {
                        const margin = calculateMargin(
                          item.replace_price || item.remove_price || item.detach_reset_price,
                          product.unit_price
                        );
                        const isSelected = selectedItems.includes(product.id);
                        const isMapped = existingMappings.some(m => m.price_list_item === product.id);
                        
                        return (
                          <tr key={product.id}>
                            <td>
                              <Checkbox
                                checked={isSelected}
                                disabled={isMapped}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems([...selectedItems, product.id]);
                                  } else {
                                    setSelectedItems(selectedItems.filter(id => id !== product.id));
                                  }
                                }}
                              />
                            </td>
                            <td>
                              <Typography level="body-sm">
                                {product.product_name}
                                {isMapped && (
                                  <Chip size="sm" color="success" variant="soft" sx={{ ml: 1 }}>
                                    <CheckCircleIcon size={14} /> Mapped
                                  </Chip>
                                )}
                              </Typography>
                            </td>
                            <td>{product.unit_of_measure}</td>
                            <td>{formatPrice(product.unit_price)}</td>
                            <td>
                              {margin && (
                                <Chip 
                                  size="sm" 
                                  color={margin.amount > 0 ? 'success' : 'danger'}
                                  variant="soft"
                                >
                                  {margin.percent.toFixed(0)}%
                                </Chip>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  
                  {priceListItems.length === 0 && !loading && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography level="body-md">
                        {selectedDistributor ? 'No products found' : 'Select a distributor'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="solid" 
            color="primary"
            loading={loading}
            disabled={selectedItems.length === 0}
            onClick={handleCreateMappings}
          >
            Create {selectedItems.length} Mapping{selectedItems.length !== 1 ? 's' : ''}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}